/**
 * View manager
 * @module
 */
var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var ApiEvents = require('../../api/api_events');
var Condition = require('../lib/condition');
var DateEnv = require('../env/date');
var di = require('../di');
var enums = require('../constants/enums');
var eventTypes = require('../constants/event_types');
var Flux = require('../flux');
var logger = require('../utils/logger');
var Tags = require('../tags');

var EventDataStore = di.get('stores/event_data');
var PluginsStore = di.get('stores/plugins');
var RumStore = di.get('stores/rum');
var ViewStore = di.get('stores/view');
var ViewDataStore = di.get('stores/view_data');

/**
 * Map of provider to provided value
 * @typedef {Object} ViewTicket
 */

/**
 * Evaluates and persists the view tags for a specific view
 * @param {View} view
 */
exports.parseViewTags = function(view:View) {
  var tagValues = exports.evaluateViewTags(view);
  exports.setParsedViewTags(view.id, tagValues);
};

/**
 * Evaluates and persists the view tags for all active views
 */
exports.updateAllViewTags = function() {
  var activeViewStates = ViewStore.getActiveViewStates();
  _.each(activeViewStates, function (viewState) {
    var view = ViewDataStore.get(viewState.id);
    exports.parseViewTags(view);
  });
};

/**
 * Retrieves value for ViewTags, usually by executing a selector on the DOM, executing JS or parsing the URL
 * @param {View} view
 * @return {Object.<string, *>} map of evaluated viewTag name => value
 */
exports.evaluateViewTags = function(view:View): Record<string, any> {
  if (!view.tags) {
    return {};
  }

  var tagValues = _.reduce(view.tags, function(res, tag) {
    try {
      res[tag.apiName] = Tags.getTagValue(tag);
    } catch(error) {
      if (error instanceof Tags.Error) {
        logger.warn('Page / Ignoring unparseable tag', tag, error);
      } else {
        logger.error(error);
      }
    }

    return res;
  }, {});

  return tagValues;
};

/**
 * Create a new view ticket dynamically based live values from data providers
 * @returns {module:core/view/index~ViewTicket}
 */
exports.createViewTicket = function(): import('core/view/index').ViewTicket {
  var viewTicket = {} as any;
  _.each(PluginsStore.getAllPlugins(enums.PluginTypes.viewProviders), function(provider) {
    viewTicket[provider.provides] = di.evaluate(provider.getter);
  });
  return viewTicket;
};

/**
 * Creates an entry for a view in the view store, which is responsible for holding view state
 * @param {View[]} views
 */
exports.registerViews = function(views:View[], forceClean?:boolean) {
  Flux.dispatch(actionTypes.REGISTER_VIEWS, {
    views: views,
    forceClean,
  });
};

/**
 * Performs special tasks for views activated via the API, then calls activateMultiple
 * @param {View} view
 * @param {Object} [metadata]
 */
exports.activateViaAPI = function(view:View, metadata?:any) {
  // manually passed metadata will overwrite parsed view tags
  if (metadata) {
    exports.setUserSuppliedViewTags(view.id, metadata);
  }
  exports.activateMultiple([view], metadata);
};

/**
 * Gets the views for a given activationType, and calls activateMultiple
 * @param {string} activationType
 */
exports.getViewsAndActivate = function(activationType:string) {
  var viewList = ViewDataStore.getAllViewsForActivationType(activationType);
  exports.activateMultiple(viewList);
};

/**
 * Evaluate and activate (if necessary) a set of views
 * Emit a viewsActivated event to trigger batch layer decision in Controller.
 * @param {View[]} viewList
 * @param {Object} [metadata] This is only used when the activate API is called.
 * @returns {View[]} the list of views that actually got activated
 */
// Despite the legacy-influenced name, this function is actually a "trigger" that causes the client to decide
// whether to activate or deactivate views.
exports.activateMultiple = function(viewList:View[], metadata?:any): View[] {
  var viewsToActivate = [];

  _.each(viewList, function(view) {
    var viewState = ViewStore.getViewState(view.id);
    var viewTicket = exports.createViewTicket();
    (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('setupViewActivation');
    var staticConditionsResult;

    if (viewState.isActive) {
      if (view.deactivationEnabled) {
        try {
          if (!exports.hasValidStaticConditions(view, viewTicket)) {
            exports.deactivate(view);
          }
        } catch(error) {
          logger.error('Page / Error evaluating whether to deactivate page ', exports.description(view), error);
        }
      } else {
        logger.log('Not activating Page, already active ', exports.description(view));
      }
      return;
    }

    try {
      // viewState.isActive must be false or null
      staticConditionsResult = exports.hasValidStaticConditions(view, viewTicket);
      if (!staticConditionsResult) {
        if (!_.isBoolean(viewState.isActive)) {
          exports.setViewActiveState(view, false);
        }
        logger.log('Page / Failed to match page conditions for ' + exports.description(view), view.staticConditions);
        return;
      }
    } catch(error) {
      if (!_.isBoolean(viewState.isActive)) {
        exports.setViewActiveState(view, false);
      }
      logger.error('Page / Error evaluating whether to activate page ', exports.description(view), error);
      return;
    }

    viewsToActivate.push(view);
    // update the View state `isActive`
    exports.setViewActiveState(view, true);
    logger.log('Activated Page', exports.description(view));

    ApiEvents.emitViewActivated({
      view: view,
      metadata: metadata,
    });

    if (RumStore.getSampleRum()) {
      // Legacy pages had no activationType for immediate type pages
      var activationTypeToLog = view.activationType || enums.ViewActivationTypes.immediate;
      Flux.dispatch(actionTypes.RECORD_ACTIVATION_TYPE_USAGE, {
        activationType: activationTypeToLog,
        entityId: view.id,
      });
    }
    // setup event tracking
    var eventsToRegister = EventDataStore.getByPageId(view.id);
    exports.toggleEventImplementations(eventsToRegister, true);
    (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('setupViewActivation');
  });

  if (!_.isEmpty(viewsToActivate)) {
    // Controller.onViewsActivated which batch decides all relevant layers
    ApiEvents.emitViewsActivated({
      views: viewsToActivate,
    });
  }
  return viewsToActivate;
};

/**
 * Deactivate a view, cleanup any event listeners
 *
 * @param {View} view
 */
exports.deactivate = function(view:View) {
  var viewState = ViewStore.getViewState(view.id);
  if (!viewState.isActive) {
    logger.log('Not deactivating Page, already inactive ', exports.description(view));
    return;
  }
  // update the View state `isActive`
  exports.setViewActiveState(view, false);
  logger.log('Deactivated Page', exports.description(view));

  ApiEvents.emitPageDeactivated({
    page: view,
  });

  // setup event tracking
  var eventsToUnregister = EventDataStore.getByPageId(view.id);
  exports.toggleEventImplementations(eventsToUnregister, false);
};

/**
 * Sets a view as being active
 * @param {View} view
 * @param {boolean} isActive
 */
exports.setViewActiveState = function(view:View, isActive:boolean) {
  Flux.dispatch(actionTypes.SET_VIEW_ACTIVE_STATE, {
    view: view,
    timestamp: DateEnv.now(),
    isActive: isActive,
  });
};

exports.setGlobalTags = function(tags) {
  Flux.dispatch(actionTypes.SET_GLOBAL_TAGS, tags);
};

/**
 * Set the view parsed view tags
 * @param {string} pageId
 * @param {Object} tags
 */
exports.setParsedViewTags = function(pageId:string, tags:any) {
  Flux.dispatch(actionTypes.UPDATE_PARSED_VIEW_METADATA, {
    pageId: pageId,
    metadata: tags,
  });
};

/**
 * Set which tag fields are user set. This is useful
 * to set manually provided tags which should take priority over
 * subsequent attempts to parse tags
 *
 * @param {string} pageId
 * @param {Object} tags
 */
exports.setUserSuppliedViewTags = function(pageId:string, tags:any) {
  Flux.dispatch(actionTypes.UPDATE_USER_SUPPLIED_METADATA, {
    pageId: pageId,
    metadata: tags,
  });
};


/**
 * Check if the static conditions associated with action view are valid
 * @param {View} view
 * @param {module:view/index~ViewTicket} viewTicket
 * @returns {boolean}
 */
exports.hasValidStaticConditions = function(view:View, viewTicket:import('core/view/index').ViewTicket): boolean {
  var typesUsed = {} as any;
  if (_.isEmpty(view.staticConditions)) {
    return true;
  }
  var matchersMap = PluginsStore.getAllPlugins(enums.PluginTypes.viewMatchers);
  logger.groupCollapsed('Page / Evaluating staticConditions:', view.staticConditions);
  logger.debug('Matching to current value:', viewTicket);
  var conditionsPassing = Condition.evaluate(view.staticConditions, function(condition) {
    var type = condition.type;
    var matcher = matchersMap[type];
    if (!matcher) {
      throw new Error('Page / No matcher found for type=' + type);
    }
    // Collect the data to send to RUM whether condition matches or not
    if (matcher) {
      if (!typesUsed[condition.type]) {
        typesUsed[condition.type] = true;
      }
    }

    return matcher.match(viewTicket, condition);
  });
  logger.groupEnd();

  if (RumStore.getSampleRum() && conditionsPassing) {
    // Send conditions data to RUM only if view is active
    Flux.dispatch(actionTypes.RECORD_VIEW_FEATURE_USAGE, {
      featuresUsed: _.keys(typesUsed),
      entityId: view.id,
    });
  }

  return conditionsPassing;
};

/**
 * Get descriptive string for a view (for logging)
 * @param {View} view
 * @returns {string}
 */
exports.description = function(view:View): string {
  return '"' + view.name + '" (' + view.id + ')';
};

/**
 * Attaches or detaches eventImplementations based on some array of Events
 * @param {Event[]} events
 * @param {boolean} shouldEnable
 * @private
 */
exports.toggleEventImplementations = function(events:Event[], shouldEnable:boolean) {
  _.forEach(events, function(event) {
    // CUSTOM events specifically don't have an eventImplementation
    // TODO(tyler): come up with a cleaner implementation
    if (event.eventType === eventTypes.CUSTOM) {
      return;
    }
    var implementation = PluginsStore.getPlugin(enums.PluginTypes.eventImplementations, event.eventType);
    if (!implementation) {
      logger.warn('No implementation found for event type:', event.eventType, 'needed for event:', event);
    } else {
      if (shouldEnable) {
        implementation.attach(event);
      } else {
        implementation.detach(event);
      }
    }
  });
};

/**
 * Helper function to determine whether a given activationType has activationType
 * immediate.
 * @param {string} [activationType]
 * @returns {boolean}
 */
exports.isActivationTypeImmediate = function(activationType?:string): boolean {
  return activationType === enums.ViewActivationTypes.immediate ||
         // Legacy value for immediate types
         !activationType;
};

/**
 * Helper function to determine whether a given activationType should trigger
 * (ie, have its conditions evaluated) immediately.
 * @param {string} [activationType]
 * @returns {boolean}
 */
exports.shouldTriggerImmediately = function(activationType?:string): boolean {
  return activationType === enums.ViewActivationTypes.DOMChanged ||
         activationType === enums.ViewActivationTypes.URLChanged ||
         activationType === enums.ViewActivationTypes.immediate ||
         // Legacy value for immediate types
         !activationType;
};

/**
 * @param  {string} id
 * @param  {string} apiName
 * @returns {View}
 */
exports.create = function(id:string, apiName:string): View {
  var view = {
    id: id,
    apiName: apiName,
    category: 'other',
  };

  if (__INVARIANT__) {
    require('../utils/invariant').isType('View', view);
  }

  return view;
};

/**
 * @param  {string} viewId
 * @returns {ViewState}
 */
exports.createState = function(viewId:string): ViewState {
  var viewState = {
    id: viewId,
    isActive: true,
    metadata: {},
    parsedMetadata: {},
    userSuppliedMetadata: {},
  };

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('ViewState', viewState);
  }

  return viewState;
};
